import { Component } from "react"
import PropTypes from "prop-types"
import { Warning } from "@mui/icons-material"
import { OPPRO_EXPERIMENT_PER_PORTFOLIO_LIMIT } from "helpers/op-pro"
import withNavigate from "hocs/withNavigate"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import SaveForm from "./components/save-form"
import UpdateDialog from "./components/update-dialog"
import { ErrorDialog } from "./components/error-dialog"
import "./style.scss"

class SaveExperiment extends Component {
  static propTypes = {
    saveAndCompare: PropTypes.func.isRequired,
    saveOptimizeAndCompare: PropTypes.func.isRequired,
    saveSmartTradeAndCompare: PropTypes.func.isRequired,
    update: PropTypes.func.isRequired,
    openDialog: PropTypes.func.isRequired,
    closeDialog: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    loadNewExperiment: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
    experiment: PropTypes.object.isRequired,
    newExperiment: PropTypes.object,
    updatedExperimentId: PropTypes.string,
    navigate: PropTypes.func.isRequired,
    portfolioId: PropTypes.string,
    portfolioAlias: PropTypes.string,
    account: PropTypes.string.isRequired,
    title: PropTypes.string,
    submitLabel: PropTypes.string,
    isAdvanceEditMode: PropTypes.bool,
    isEditMode: PropTypes.bool,
    index: PropTypes.number,
    applyAction: PropTypes.func,
    resetDataCollections: PropTypes.func.isRequired,
    rename: PropTypes.func.isRequired,
    isOneDayExperiment: PropTypes.bool,
    researchTopicId: PropTypes.string,
    loadResearchTopicNewExperiment: PropTypes.func.isRequired,
    experimentLimitReached: PropTypes.bool.isRequired,
    onSuccess: PropTypes.func,
    showPostSaveApplyOptions: PropTypes.bool,
    existingComparisonInstrument: PropTypes.object,
    searchType: PropTypes.string,
  }

  static defaultProps = {
    onSuccess: null,
    showPostSaveApplyOptions: false,
    existingComparisonInstrument: null,
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.newExperiment && this.props.newExperiment) {
      if (this.props.experiment.type === "PORTFOLIO") {
        this.goToUpload()
      } else {
        this.showSuccessDialog()
      }
    } else if (prevProps.updatedExperimentId !== this.props.updatedExperimentId) {
      this.props.close()
      this.props.reset()
      this.goToExperiments(true)
    }
  }

  update = () => {
    const { update, experiment } = this.props
    update(experiment)
  }

  rename = (name, description) => {
    const { rename, experiment, applyAction } = this.props
    experiment.name = name
    experiment.description = description

    if (applyAction) {
      applyAction(() => rename(experiment))()
    } else {
      rename(experiment)
    }
  }

  save = (name, description, applyTo = {}) => {
    this.pendingApplyTo = applyTo
    const { saveAndCompare, portfolioAlias, saveOptimizeAndCompare, saveSmartTradeAndCompare, experiment, researchTopicId, searchType = RECENT_INSTRUMENT_SEARCH_TYPES.EXPERIMENT } = this.props

    const experimentToSave = { ...experiment, name, description }
    let resourceType = "PORTFOLIO"
    let resourceId = portfolioAlias

    switch (experiment.type) {
      case "OPTIMIZATION":
        saveOptimizeAndCompare(experimentToSave, searchType)
        break
      case "SMART_TRADE":
        saveSmartTradeAndCompare(experimentToSave, researchTopicId, searchType)
        break
      default:
        if (researchTopicId) {
          resourceType = "RESEARCH_TOPIC"
          resourceId = researchTopicId
        }
        saveAndCompare(experimentToSave, resourceType, resourceId, searchType)
    }
  }

  goToUpload = () => {
    const { newExperiment, navigate, account, portfolioAlias, close, researchTopicId } = this.props
    let url = `/${account}/p/${portfolioAlias}/experiments/${newExperiment.id}/upload`
    if (researchTopicId) {
      url = `/${account}/t/${researchTopicId}/experiments/${newExperiment.id}/upload`
    }
    navigate(url)
    close()
  }

  goToExperiments = (forceRefresh) => {
    const {
      newExperiment,
      loadResearchTopicNewExperiment,
      loadNewExperiment,
      navigate,
      account,
      portfolioAlias,
      resetDataCollections,
      index,
      researchTopicId,
      experiment,
      isAdvanceEditMode,
    } = this.props

    let redirectUrl

    if (researchTopicId) {
      if (newExperiment && !isAdvanceEditMode) {
        loadResearchTopicNewExperiment(newExperiment)
      }
      redirectUrl = `/${account}/t/${researchTopicId}/experiments/summary`
    } else {
      if (newExperiment) {
        loadNewExperiment(newExperiment)
      }

      redirectUrl = `/${account}/p/${portfolioAlias}/rebalance/experiments`
    }

    if (forceRefresh) {
      resetDataCollections(index, experiment)
    }

    navigate(redirectUrl)
  }

  showSuccessDialog = () => {
    const { openDialog, experiment, newExperiment, onSuccess, closeDialog } = this.props
    if (onSuccess) {
      closeDialog()
      onSuccess(newExperiment, this.pendingApplyTo || {})
      this.pendingApplyTo = null
      return
    }

    let cancelText = "Stay in simulator"
    if (experiment.type === "OPTIMIZATION") {
      cancelText = "Stay in optimizer"
    } else if (experiment.type === "SMART_TRADE") {
      cancelText = "Stay in smart trade"
    }
    openDialog({
      title: `Would you like to view ${newExperiment.name} now?`,
      contentText: "This experiment is now saved and can be searched for within the experiment screen. Would you like to view and compare the experiment now?",
      cancelText,
      buttons: [{ text: "View now", type: "primary", action: this.goToExperiments }],
    })
  }

  render() {
    const {
      submitLabel,
      close,
      isAdvanceEditMode,
      isEditMode,
      experiment,
      isOneDayExperiment,
      experimentLimitReached,
      showPostSaveApplyOptions,
      existingComparisonInstrument,
    } = this.props

    if (experimentLimitReached) {
      return <ErrorDialog error={`You have reached the maximum number (${OPPRO_EXPERIMENT_PER_PORTFOLIO_LIMIT}) of rebalance experiments allowed for your plan. Please delete other experiments if you'd like to save a new experiment.`} close={close} />
    }

    let subContent = (
      <SaveForm
        save={isEditMode ? this.rename : this.save}
        isOneDayExperiment={isOneDayExperiment}
        experiment={experiment}
        submitLabel={submitLabel}
        close={close}
        showPostSaveApplyOptions={showPostSaveApplyOptions}
        existingComparisonInstrument={existingComparisonInstrument}
      />
    )
    if (isAdvanceEditMode) {
      subContent = <UpdateDialog update={this.update} close={close} isOneDayExperiment={isOneDayExperiment} />
    }

    return <>
      {isOneDayExperiment
        && <div className="warning-oneday">
          <Warning className="icon" />
          <div className="warning-text">This is a 1-day experiment with no historic data. This may cause certain features to not display correctly.</div>
        </div>}
      {subContent}
    </>
  }
}

export default withNavigate(SaveExperiment)
